import type { SecretListing } from "@repo/app-types/entities/secret";
import { Input } from "@repo/ui/Inputs/input";
import { AlertDialog, AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { IconTrash } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { useDeleteSecretMutation } from "../../../api/entities/secret.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { CopyValueButton } from "../integrationSettings/copyableValue.js";

interface DeleteSecretModalProps {
  /** The secret to delete; the modal is open while one is set. */
  secret: SecretListing | undefined;
  onClose: () => void;
}

export const DeleteSecretModal: FC<DeleteSecretModalProps> = (props) => {
  const { secret, onClose } = props;

  return (
    <CenteredModal
      isDismissable
      isOpen={secret !== undefined}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      {secret ? <DeleteSecretDialog secret={secret} /> : null}
    </CenteredModal>
  );
};

interface DeleteSecretDialogProps {
  secret: SecretListing;
}

/** Deletes the secret once its full name is typed. */
const DeleteSecretDialog: FC<DeleteSecretDialogProps> = (props) => {
  const { secret } = props;

  const [typedName, setTypedName] = useState("");
  const [deleteSecret, { isLoading: isDeleting }] = useDeleteSecretMutation();

  const handleDelete = async (close: () => void) => {
    try {
      await deleteSecret(secret.id).unwrap();
      close();
    } catch (error) {
      showToast({
        title: getApiErrorMessage(error, "Failed to delete the secret"),
        variant: ToastVariant.Error,
      });
    }
  };

  return (
    <AlertDialog
      variant={AlertDialogVariant.Destructive}
      title={
        <span className="flex items-center gap-2">
          <IconTrash aria-hidden className="size-5 shrink-0 text-destructive" />
          Delete {secret.name}?
        </span>
      }
      confirmText="Delete secret"
      isConfirming={isDeleting}
      // Without a handler the button is disabled, until the name matches.
      onConfirm={
        typedName === secret.name
          ? ({ close }) => void handleDelete(close)
          : undefined
      }
    >
      <div className="space-y-4">
        <p>
          Deleting this secret will remove all references to it.{" "}
          <strong className="font-medium text-foreground">
            This action can&apos;t be undone.
          </strong>
        </p>
        <p>
          Type{" "}
          <span className="inline-flex items-center gap-0.5 align-middle">
            <strong className="font-mono font-medium text-foreground">
              {secret.name}
            </strong>
            {/* Its own height, not the button's default, so it doesn't stretch the line. */}
            <CopyValueButton
              value={secret.name}
              label="secret name"
              className="h-auto"
            />
          </span>{" "}
          to confirm.
        </p>
        <Input
          accessibleLabel="Secret name"
          placeholder={secret.name}
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          autoComplete="off"
          autoFocus
        />
      </div>
    </AlertDialog>
  );
};
